import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import { Shield, Users, Award, Heart, CheckCircle, Target, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HorizontalScrollMobile from '../components/HorizontalScrollMobile';
import { openChat } from '../utils/chat';

const AboutUsPage = () => {
  useEffect(() => {
    setPageMeta(
      'About Us - Legal Flick Legal Services',
      'Learn about Legal Flick\'s mission to make professional legal services accessible and affordable for everyone. Expert guidance with innovative technology.'
    );
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header showNav={true} onChatClick={() => {}} />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full text-sm font-medium mb-6 text-blue-700">
              Trusted by 500,000+ Customers
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900">
              Making Legal Services<br />
              <span className="text-blue-600">Accessible to Everyone</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're on a mission to democratize legal services by combining expert guidance with innovative technology
            </p>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <div className="inline-flex items-center space-x-2 text-blue-600 font-semibold mb-4">
                <Target className="w-5 h-5" />
                <span>OUR MISSION</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transforming Legal Services for the Modern World
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded in 2020, Legal Flick was born from a simple observation: legal services were too expensive, too complicated, and too intimidating for most people. We knew there had to be a better way.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our founders, experienced attorneys and technology entrepreneurs, came together with a vision to leverage technology to make professional legal services accessible to everyone. What started as a small team with a big idea has grown into a platform serving over 500,000 customers nationwide.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, we're proud to offer comprehensive legal services covering business formation, intellectual property protection, estate planning, and much more—all backed by experienced legal professionals and cutting-edge technology.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">500K+</div>
                    <div className="text-sm text-gray-600">Happy Customers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Mission Points */}
          <HorizontalScrollMobile desktopCols="md:grid-cols-3" className="mt-16">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Lightbulb className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation First</h3>
              <p className="text-gray-600">
                We continuously innovate to simplify complex legal processes and deliver exceptional user experiences.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Success</h3>
              <p className="text-gray-600">
                Your success is our success. We're committed to providing outstanding support throughout your journey.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Trusted Expertise</h3>
              <p className="text-gray-600">
                Every service is backed by licensed attorneys and legal professionals with decades of experience.
              </p>
            </div>
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <HorizontalScrollMobile desktopCols="md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Trust</h3>
              <p className="text-gray-600">
                We handle your legal matters with the utmost care and confidentiality
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Accessibility</h3>
              <p className="text-gray-600">
                Legal services should be available to everyone, not just the wealthy
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Excellence</h3>
              <p className="text-gray-600">
                We maintain the highest standards in everything we deliver
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Heart className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Support</h3>
              <p className="text-gray-600">
                We're here to support you throughout your entire legal journey
              </p>
            </div>
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Commitment to You
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Building trust through transparency, innovation, and unwavering dedication
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-8">
            {/* First Content Block with Icon */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Democratizing Legal Access</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    At Legal Flick, we believe that everyone deserves access to quality legal services without the complexity and high costs traditionally associated with legal matters. Our mission is to bridge the gap between individuals, small businesses, and the legal expertise they need to protect their interests and achieve their goals.
                  </p>
                </div>
              </div>
            </div>

            {/* Quote/Highlight Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-10 text-white shadow-xl">
              <div className="flex items-center space-x-3 mb-4">
                <CheckCircle className="w-8 h-8 text-blue-200" />
                <span className="text-2xl font-bold">500,000+</span>
              </div>
              <p className="text-lg md:text-xl leading-relaxed">
                Since our founding, we've helped hundreds of thousands of customers navigate their legal journeys with confidence. Whether you're starting a business, protecting your intellectual property, planning your estate, or seeking legal guidance, we combine cutting-edge technology with experienced legal professionals to deliver exceptional results at transparent, affordable prices.
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">What Sets Us Apart</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our unwavering commitment to customer success. We don't just process documents—we partner with you to understand your unique situation and provide personalized solutions. Our platform is designed to be intuitive and user-friendly, while our support team is always ready to assist you with expert guidance when you need it most.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Peace of Mind</h3>
                <p className="text-gray-700 leading-relaxed">
                  We understand that legal matters can be stressful. That's why we've built our services around transparency, reliability, and peace of mind. Every service is backed by licensed attorneys, comprehensive accuracy guarantees, and a dedication to your satisfaction. When you choose Legal Flick, you're choosing a partner invested in your success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">500K+</div>
              <div className="text-xl text-gray-600">Customers Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-xl text-gray-600">Legal Services</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">99%</div>
              <div className="text-xl text-gray-600">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Legal Flick?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine technology, expertise, and customer service to deliver exceptional value
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Affordable Pricing</h3>
              <p className="text-gray-600">
                Quality legal services at a fraction of traditional legal fees. No hidden costs or surprise charges.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Expert Support</h3>
              <p className="text-gray-600">
                Our team of legal professionals is available to answer your questions and guide you through the process.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Fast & Reliable</h3>
              <p className="text-gray-600">
                We process your documents quickly and accurately, keeping you informed every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not just another legal services provider—we're your partner in navigating life's legal complexities
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Technology-Powered Platform</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our proprietary platform automates complex legal processes, reducing costs and turnaround times while maintaining the highest quality standards. What used to take weeks now takes days.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Legal Team</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Every document is reviewed by licensed attorneys. Every service is designed by legal experts. You get the benefit of professional legal knowledge at a fraction of traditional costs.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No hidden fees, no surprise charges. You'll know exactly what you're paying before you commit. Our pricing is straightforward and competitive, designed to be accessible to all.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Support</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our customer support team is available to guide you through every step. Whether you need help understanding a document or choosing the right service, we're here to help.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">State-Specific Compliance</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Legal requirements vary by state. Our platform automatically adjusts to ensure your documents meet all state-specific requirements, keeping you fully compliant.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                    6
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Secure & Confidential</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your information is protected with bank-level encryption. We take privacy seriously and never share your data. Your legal matters remain completely confidential.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Legal Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From starting a business to planning your estate, we offer a complete range of legal services to meet your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">🏢</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Business Formation</h3>
              <p className="text-gray-600 mb-4">
                Start your business with confidence. We handle LLC, Corporation, DBA, and Nonprofit formations in all 50 states.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  LLC Formation
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Corporation Setup
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  DBA Registration
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">⚖️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intellectual Property</h3>
              <p className="text-gray-600 mb-4">
                Protect your ideas and creations. Trademark registration, copyright protection, and provisional patents.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Trademark Registration
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Copyright Protection
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Provisional Patents
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Estate Planning</h3>
              <p className="text-gray-600 mb-4">
                Secure your family's future. Professional will creation, trust establishment, and power of attorney documents.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Last Will & Testament
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Living Trusts
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Power of Attorney
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Business Compliance</h3>
              <p className="text-gray-600 mb-4">
                Stay compliant and avoid penalties. Annual reports, registered agent services, and ongoing compliance support.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Annual Report Filing
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Registered Agent
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Compliance Alerts
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">👨‍⚖️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Attorney Services</h3>
              <p className="text-gray-600 mb-4">
                Unlimited access to experienced attorneys. Get legal advice and document review for business and personal matters.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Business Attorney Plans
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Personal Attorney Plans
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Document Review
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-blue-600 text-4xl mb-4">📄</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Documents</h3>
              <p className="text-gray-600 mb-4">
                Access thousands of legal documents. Leases, contracts, agreements, and more—all customized for your state.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Residential Leases
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Operating Agreements
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Property Deeds
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-12 lg:p-16 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-6">
                Our Commitment to You
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                At Legal Flick, we're committed to providing exceptional legal services that are accessible, affordable, and reliable. Every customer matters to us, and we're dedicated to earning your trust every single day.
              </p>
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div>
                  <div className="text-5xl font-bold mb-2">100%</div>
                  <div className="text-blue-200">Satisfaction Guarantee</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">24/7</div>
                  <div className="text-blue-200">Customer Support</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">50+</div>
                  <div className="text-blue-200">Legal Services</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Ready to Get Legal Help?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Join over 500,000 satisfied customers who trust Legal Flick for their legal needs. Start your journey today and experience the difference professional legal services can make.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
                onClick={openChat}
              >
                  Chat Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Our Team
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Questions? Call us at <a href="tel:+18555484511" className="font-semibold text-blue-600 hover:text-blue-700">+1 (855) 548-4511</a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUsPage;